'use client'

import type { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import { X } from 'lucide-react'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import type { InteractiveButton, PlayerConfig } from '../../../types/interactiveVideo'
import { type InteractiveVideoPlayerApi } from '../InteractiveVideoPlayer'
import VideoInfoPanelStyles from '../modal/video-info-panel/VideoInfoPanelStyles'
import { useFrameFullscreenExit } from '../shadow-host-portal/useFrameFullscreenExit'
import { withoutCompletedSurveyButtons, withoutCompletedSurveyScenarioButtons } from '../survey/completedSurveyButtons'
import BoxBottomBar from './BoxBottomBar'
import { CHOICE_AUTO_OPEN_REMAIN_SEC, TWO_COLUMN_BREAKPOINT, VIDEO_CONTAIN_BREAKPOINT, scrollDecisionIntoFocus } from './boxConstants'
import BoxInfoPanel from './BoxInfoPanel'
import BoxVideoColumn from './BoxVideoColumn'

export type BoxModalProps = {
  scenarioId: string
  config: PlayerConfig
  nodes: ScenarioNodeWithVideoUrl[]
  currentNodeKey: string
  onClose: () => void
  onVideoEnd: (nodeId: string) => void
  onVideoChange: (nodeId: string) => void
  onButtonClick: (button: InteractiveButton) => void
  onCtaClick?: () => void
}

/**
 * BOX B: 左に縦型動画、右にストーリーの文脈と進捗を置く展開ビュー。
 * PCでは選択肢を再生開始時から動画下部へ重ね、狭い画面では動画終了後に
 * 動画下の白いボトムシートへ表示する。
 */
const BoxModal: FC<BoxModalProps> = ({
  scenarioId,
  config,
  nodes,
  currentNodeKey,
  onClose,
  onVideoEnd,
  onVideoChange,
  onButtonClick,
  onCtaClick,
}) => {
  const [isTwoColumn, setIsTwoColumn] = useState(false)
  // タブレット幅の1カラムでは動画を全体表示にする（cover だと上下が切れてテロップが消える）
  const [isContainFit, setIsContainFit] = useState(false)
  const [isMobileDetail, setIsMobileDetail] = useState(false)
  const [isDecisionVisible, setIsDecisionVisible] = useState(false)
  // 動画に重ねる選択肢を開いているか。再生中は畳んでピル1行だけを出す（ADR-0016 の運用調整）
  const [isChoiceOpen, setIsChoiceOpen] = useState(false)
  const [isSpCollapseHovered, setIsSpCollapseHovered] = useState(false)
  const playerApiRef = useRef<InteractiveVideoPlayerApi | null>(null)
  // CVで別タブへ移る間、裏で動画が進み続けないように止める。
  // 選択肢は残り数秒で出るので、CVは再生中に押され得る。
  const handlePauseVideo = () => playerApiRef.current?.pauseVideo()
  // 動画紐づきアンケートの描画先（右パネル）。プレイヤーが portal で流し込む。
  // ref ではなく state で持つのは、要素がマウントされた時点で再レンダリングを起こして
  // portal を張るため（初回レンダリング時点では DOM がまだ無い）。
  const [surveySlot, setSurveySlot] = useState<HTMLDivElement | null>(null)
  // 答え終えたアンケートの箱。右パネル・動画上の選択肢から、そこへ進むボタンを消す
  const [completedSurveyNodeKeys, setCompletedSurveyNodeKeys] = useState<Set<string>>(() => new Set())
  // 決定エリア（設問＋選択肢）。本文が長いと折り返し以下に沈んで気づかれないため、
  // 画面内に入っているかを監視して誘導を出す。
  const decisionRef = useRef<HTMLDivElement | null>(null)
  const scrollAreaRef = useRef<HTMLDivElement | null>(null)
  const [isDecisionInView, setIsDecisionInView] = useState(true)

  useEffect(() => {
    const sync = () => {
      setIsTwoColumn(window.innerWidth >= TWO_COLUMN_BREAKPOINT)
      setIsContainFit(window.innerWidth >= VIDEO_CONTAIN_BREAKPOINT)
    }
    sync()
    window.addEventListener('resize', sync)
    return () => window.removeEventListener('resize', sync)
  }, [])

  useEffect(() => {
    setIsDecisionVisible(false)
    setIsDecisionInView(true)
    setIsMobileDetail(false)
    setIsChoiceOpen(false)
  }, [currentNodeKey])

  /**
   * 動画が終わった瞬間にパネルの役割は「読む」から「決める」に変わるが、本文（出演者
   * プロフィール＋動画テキスト2枠）が長いと選択肢が折り返し以下に沈んで気づかれない。
   * 顧客が入力する文章量に左右される問題なので、位置ではなく可視状態で解決する。
   *
   * 1. 選択肢が出たら、そこまでスクロールして見せる（大半のケースはこれで足りる）
   * 2. その後スクロールで画面外に出たら、追従ボタンで戻れるようにする
   */
  useEffect(() => {
    // PCは選択肢を動画上へ常時表示するため、右パネルへの自動スクロールは不要。
    if (!isDecisionVisible || isTwoColumn) return
    const element = decisionRef.current
    const container = scrollAreaRef.current
    if (!element || !container) return

    scrollDecisionIntoFocus(container, element)

    const observer = new IntersectionObserver(
      ([entry]) => setIsDecisionInView(entry.isIntersecting),
      // 少しでも見えていれば「見えている」とみなす。全体が入るまで誘導を出し続けると、
      // 選択肢が多いシナリオでボタンが消えなくなる。
      { root: container, threshold: 0 },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [isDecisionVisible, isTwoColumn])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  // iframe 埋め込みページ（/embed）では、ブラウザ側の操作で全画面が解除されたら閉じる
  useFrameFullscreenExit(true, onClose)

  const unmutedConfig = useMemo(() => ({ ...config, muted: false }), [config])
  const currentNode = nodes.find((node) => node.nodeKey === currentNodeKey) ?? null
  // タイトル・出演者・動画テキストの表示は共通基盤（VideoInfoContent）に任せる。
  // ここで持つのは aria-label 用の文字列だけ。
  const title = currentNode?.video?.title || currentNode?.title || 'インタラクティブストーリー'
  const ctaUrl = currentNode?.video?.ctaUrl ?? null
  const ctaLabel = currentNode?.video?.ctaLabel ?? null
  const choiceButtons = withoutCompletedSurveyScenarioButtons(currentNode?.buttons ?? [], completedSurveyNodeKeys)
  const overlayButtons = withoutCompletedSurveyButtons(
    config.nodes.get(currentNodeKey)?.buttons ?? [],
    completedSurveyNodeKeys,
  )
  const branchQuestion =
    currentNode?.config && typeof (currentNode.config as Record<string, unknown>).branchQuestionText === 'string'
      ? ((currentNode.config as Record<string, unknown>).branchQuestionText as string)
      : '次に見たい動画を選んでください'
  // 終了後の「もう一度見る」。今の動画を頭から再生し直し、選択肢は終了3秒前にまた開く
  const handleReplay = () => {
    setIsDecisionVisible(false)
    setIsChoiceOpen(false)
    playerApiRef.current?.replay()
  }

  const handleVideoEnd = (nodeId: string) => {
    if (nodeId === currentNodeKey) {
      setIsDecisionVisible(true)
      setIsChoiceOpen(true)
    }
    onVideoEnd(nodeId)
  }

  const handleVideoChange = (nodeId: string) => {
    setIsChoiceOpen(false)
    setIsDecisionVisible(false)
    setIsMobileDetail(false)
    onVideoChange(nodeId)
  }

  /**
   * 残り CHOICE_AUTO_OPEN_REMAIN_SEC 秒になったら選択肢を開く。
   *
   * 毎秒数回呼ばれるので state は「閉→開」の1回しか触らない（同じ値の setState は
   * React 側で再レンダリングされないため、それ以降の呼び出しは実質何もしない）。
   */
  const handleTimeUpdate = (currentTime: number, duration: number) => {
    if (!Number.isFinite(duration) || duration <= 0) return
    if (duration - currentTime <= CHOICE_AUTO_OPEN_REMAIN_SEC) setIsChoiceOpen(true)
  }

  return (
    <div
      data-testid="box-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        // 背景幕はタイル型モーダル(PCVideoModal)と同じ半透明の見え方に揃える。
        // 背面の入口バナーはモーダル表示中だけ非表示にし、上端の黒い帯を防ぐ。
        background: isTwoColumn ? 'rgba(0, 0, 0, 0.8)' : 'rgba(15, 23, 42, 0.64)',
        backdropFilter: isTwoColumn ? 'none' : 'blur(10px)',
        WebkitBackdropFilter: isTwoColumn ? 'none' : 'blur(10px)',
        pointerEvents: 'auto',
        /*
         * フォントはタイル型(PCVideoModal)・SP動画モーダルと完全に同一の指定にする。
         * BOX だけ Inter を先頭に置いていたため、英数字が他の埋め込みと違う書体で出ていた。
         * Shadow DOM は親ページの font-family を継承するので、ここで明示して埋め込み先ごとの
         * 見た目のブレも防ぐ（Noto 未到着の間は OS 既定のサンセリフでフォールバック）。
         */
        fontFamily:
          '"Noto Sans JP", -apple-system, BlinkMacSystemFont, "Hiragino Kaku Gothic ProN", "Yu Gothic", sans-serif',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility',
      }}
    >
      <div style={{ position: 'absolute', inset: 0 }} onClick={onClose} aria-hidden="true" />

      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: isTwoColumn ? 'row' : 'column',
          // 高さはタイル型(PCVideoModal)と同じ。幅は「動画幅＋テキスト幅」に合わせて
          // 縮める（width: auto）ので、動画の左右にグレーの余白が出ない。
          width: isTwoColumn ? 'auto' : '100vw',
          maxWidth: isTwoColumn ? 'calc(100vw - 48px)' : undefined,
          height: isTwoColumn ? '92vh' : '100dvh',
          maxHeight: isTwoColumn ? '850px' : undefined,
          overflow: 'hidden',
          border: 'none',
          borderRadius: isTwoColumn ? '12px' : 0,
          background: isTwoColumn ? '#fff' : '#1a1a1a',
          boxShadow: isTwoColumn
            ? 'none'
            : '0 42px 90px -30px rgba(2, 6, 23, 0.75), 0 20px 45px -28px rgba(15, 23, 42, 0.55)',
          animation: 'moovy-box-open 260ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        {/* .info-* のスタイル定義。閉じるボタンでも使うので、パネルの有無に関わらず注入する。 */}
        <VideoInfoPanelStyles />

        <style>{`
          @keyframes moovy-box-open { from { opacity: 0; transform: translateY(12px) scale(.985) } to { opacity: 1; transform: translateY(0) scale(1) } }

          .box-bottom-cta {
            position: absolute;
            right: 0;
            bottom: 19px;
            left: 0;
            z-index: 20;
            padding: 16px 40px;
          }

          /* アンケートの固定バーが出たら、CVボタンをその一段上へ積む。 */
          .info-panel:has([data-testid="panel-survey-optin"]) .box-bottom-cta {
            bottom: 95px;
          }

          .info-panel:has([data-testid="panel-survey-optin"]) .box-info-content {
            padding-bottom: 160px;
          }

          /* 回答シートの展開中はアンケートへ集中させる。 */
          .info-panel:has([data-testid="panel-survey-sheet"]) .box-bottom-cta,
          .info-panel:has([data-testid="panel-survey-sheet"]) .box-bottom-fade {
            display: none;
          }

          @media (max-width: 767px) {
            .box-bottom-cta {
              padding-right: 21px;
              padding-left: 21px;
            }
          }
        `}</style>

        {isTwoColumn && (
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            /*
             * 見た目とホバーはタイル型と同じ .info-close-button に任せる（BOX 独自の
             * 白い丸ボタンをやめて埋め込み全体で統一する）。z-index だけは上書きが要る:
             * クラスの z-index: 1 は .info-panel の内側に置かれる前提の値で、BOX では
             * このボタンが動画・パネルより前に並ぶ兄弟なので、後続要素に隠れてしまう。
             */
            className="info-close-button"
            style={{ zIndex: 40 }}
          >
            <X size={20} />
          </button>
        )}

        <BoxVideoColumn
          scenarioId={scenarioId}
          unmutedConfig={unmutedConfig}
          config={config}
          overlayButtons={overlayButtons}
          branchQuestion={branchQuestion}
          videoFit={!isTwoColumn && isContainFit ? 'contain' : undefined}
          isTwoColumn={isTwoColumn}
          isMobileDetail={isMobileDetail}
          isChoiceOpen={isChoiceOpen}
          isDecisionVisible={isDecisionVisible}
          surveySlot={surveySlot}
          playerApiRef={playerApiRef}
          onClose={onClose}
          onVideoEnd={handleVideoEnd}
          onVideoChange={handleVideoChange}
          onButtonClick={onButtonClick}
          onTimeUpdate={handleTimeUpdate}
          onSurveyGateComplete={(nodeId) =>
            setCompletedSurveyNodeKeys((prev) => (prev.has(nodeId) ? prev : new Set(prev).add(nodeId)))
          }
          onOpenChoice={() => setIsChoiceOpen(true)}
          onCollapseChoice={() => setIsChoiceOpen(false)}
        />

        {currentNode && (isTwoColumn || isMobileDetail) && (
          <BoxInfoPanel
            currentNode={currentNode}
            isTwoColumn={isTwoColumn}
            isDecisionVisible={isDecisionVisible}
            choiceButtons={choiceButtons}
            branchQuestion={branchQuestion}
            isDecisionInView={isDecisionInView}
            ctaUrl={ctaUrl}
            ctaLabel={ctaLabel}
            scrollAreaRef={scrollAreaRef}
            decisionRef={decisionRef}
            setSurveySlot={setSurveySlot}
            onReplay={handleReplay}
            onCloseDetail={() => setIsMobileDetail(false)}
            onScrollToDecision={() => {
              const container = scrollAreaRef.current
              const element = decisionRef.current
              if (container && element) scrollDecisionIntoFocus(container, element)
            }}
            onCtaClick={onCtaClick}
            onPauseVideo={handlePauseVideo}
          />
        )}

        {/* 詳細パネル表示中は操作帯の中身が全て隠れるので、帯ごと出さない */}
        {!isTwoColumn && !isMobileDetail && currentNode && (
          <BoxBottomBar
            title={title}
            config={config}
            overlayButtons={overlayButtons}
            choiceButtons={choiceButtons}
            branchQuestion={branchQuestion}
            isChoiceOpen={isChoiceOpen}
            isDecisionVisible={isDecisionVisible}
            isSpCollapseHovered={isSpCollapseHovered}
            ctaUrl={ctaUrl}
            ctaLabel={ctaLabel}
            playerApiRef={playerApiRef}
            onOpenDetail={() => setIsMobileDetail(true)}
            onOpenChoice={() => setIsChoiceOpen(true)}
            onCollapseChoice={() => setIsChoiceOpen(false)}
            onCollapseHoverChange={setIsSpCollapseHovered}
            onCtaClick={onCtaClick}
            onPauseVideo={handlePauseVideo}
          />
        )}
      </div>
    </div>
  )
}

export default BoxModal
